import apiClient from "@/api/client";
import type { Car, CarAssignment, CarIssueComment, CarIssueReport, CarReservation, FleetTodoCount } from "../types";

export const fleetApi = {
  // Cars
  listCars: (params?: Record<string, string>) =>
    apiClient.get<{ data: Car[] }>("/fleet/cars/", { params }).then((r) => r.data.data),

  getCar: (id: string) =>
    apiClient.get<{ data: Car }>(`/fleet/cars/${id}/`).then((r) => r.data.data),

  createCar: (data: {
    brand: string;
    model: string;
    license_plate: string;
    year?: number;
    color?: string;
    vin?: string;
    notes?: string;
  }) =>
    apiClient.post<{ data: Car }>("/fleet/cars/", data).then((r) => r.data.data),

  updateCar: (id: string, data: Partial<Car>) =>
    apiClient.patch<{ data: Car }>(`/fleet/cars/${id}/`, data).then((r) => r.data.data),

  deleteCar: (id: string) => apiClient.delete(`/fleet/cars/${id}/`),
  sellCar: (id: string) => apiClient.post(`/fleet/cars/${id}/sell/`),

  assignCar: (id: string, data: {
    holder_id: string;
    date_from: string;
    date_to?: string;
    notes?: string;
  }) =>
    apiClient
      .post<{ data: { car: Car; assignment: CarAssignment } }>(`/fleet/cars/${id}/assign/`, data)
      .then((r) => r.data.data),

  returnToPool: (id: string, data: {
    pool_location: "pool_opole" | "pool_wroclaw";
    date?: string;
    notes?: string;
  }) =>
    apiClient
      .post<{ data: { car: Car; assignment: CarAssignment } }>(`/fleet/cars/${id}/return-to-pool/`, data)
      .then((r) => r.data.data),

  movePool: (id: string, data: {
    pool_location: "pool_opole" | "pool_wroclaw";
    date?: string;
    notes?: string;
  }) =>
    apiClient
      .post<{ data: { car: Car; assignment: CarAssignment } }>(`/fleet/cars/${id}/move-pool/`, data)
      .then((r) => r.data.data),

  getHistory: (id: string) =>
    apiClient
      .get<{ data: CarAssignment[] }>(`/fleet/cars/${id}/history/`)
      .then((r) => r.data.data),

  // Reservations
  listReservations: (params?: Record<string, string>) =>
    apiClient
      .get<{ data: CarReservation[] }>("/fleet/car-reservations/", { params })
      .then((r) => r.data.data),

  createReservation: (data: {
    car: string;
    date_from: string;
    date_to: string;
    notes?: string;
  }) =>
    apiClient
      .post<{ data: CarReservation }>("/fleet/car-reservations/", data)
      .then((r) => r.data.data),

  deleteReservation: (id: string) => apiClient.delete(`/fleet/car-reservations/${id}/`),

  // Issue reports
  listIssueReports: (params?: Record<string, string>) =>
    apiClient
      .get<{ data: CarIssueReport[] }>("/fleet/issue-reports/", { params })
      .then((r) => r.data.data),

  createIssueReport: (data: { car: string; title: string; description?: string; priority?: string }) =>
    apiClient
      .post<{ data: CarIssueReport }>("/fleet/issue-reports/", data)
      .then((r) => r.data.data),

  updateIssueReport: (id: string, data: Partial<CarIssueReport>) =>
    apiClient
      .patch<{ data: CarIssueReport }>(`/fleet/issue-reports/${id}/`, data)
      .then((r) => r.data.data),

  deleteIssueReport: (id: string) => apiClient.delete(`/fleet/issue-reports/${id}/`),

  // Issue comments
  listComments: (reportId: string) =>
    apiClient
      .get<{ data: CarIssueComment[] }>("/fleet/issue-comments/", { params: { report: reportId } })
      .then((r) => r.data.data),

  createComment: (data: { report: string; text: string; mentioned_users?: string[] }) =>
    apiClient
      .post<{ data: CarIssueComment }>("/fleet/issue-comments/", data)
      .then((r) => r.data.data),

  deleteComment: (id: string) => apiClient.delete(`/fleet/issue-comments/${id}/`),

  // Fleet todo count (for sidebar badge)
  getTodoCount: () =>
    apiClient.get<{ data: FleetTodoCount }>("/fleet/todo-count/").then((r) => r.data.data),
};
